import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Check, 
  X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise, isStripeConfigured } from '@/config/stripe';
import StripeCardForm from './StripeCardForm';

interface PaymentMethod {
  id: number;
  method_type: 'card' | 'upi' | 'bank_account' | 'wallet';
  provider: string;
  card_last4?: string;
  card_brand?: string;
  card_exp_month?: number;
  card_exp_year?: number;
  upi_id?: string;
  bank_account_last4?: string;
  bank_name?: string;
  account_holder_name?: string;
  ifsc_code?: string;
  wallet_type?: string;
  is_default: boolean;
  is_active: boolean;
  nickname?: string;
  created_at?: string;
}

interface PaymentMethodsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PaymentMethodsModal: React.FC<PaymentMethodsModalProps> = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [selectedMethodType, setSelectedMethodType] = useState<'card' | 'upi' | 'bank_account' | 'wallet' | null>(null);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      setPaymentMethods(data.payment_methods || []);
      setError(null);
    } catch (_err) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/payment-methods/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete payment method');
      }

      await fetchPaymentMethods();
    } catch (_err) {
      console.error('Error deleting payment method:', err);
      alert('Failed to delete payment method');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/payment-methods/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_default: true })
      });

      if (!response.ok) {
        throw new Error('Failed to set default payment method');
      }

      await fetchPaymentMethods();
    } catch (_err) {
      console.error('Error setting default payment method:', err);
      alert('Failed to set default payment method');
    }
  };

  const handleUpdateNickname = async (id: number, nickname: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/payment-methods/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nickname })
      });

      if (!response.ok) {
        throw new Error('Failed to update nickname');
      }

      await fetchPaymentMethods();
      setEditingMethod(null);
    } catch (_err) {
      console.error('Error updating nickname:', err);
      alert('Failed to update nickname');
    }
  };

  const handleAddPaymentMethod = async (methodType: 'card' | 'upi' | 'bank_account' | 'wallet') => {
    try {
      setError(null);
      setSelectedMethodType(methodType);
      
      // For cards, show Stripe form if configured
      if (methodType === 'card' && isStripeConfigured()) {
        setShowStripeForm(true);
        return;
      }
      
      // For other methods, show coming soon message
      if (methodType === 'upi' || methodType === 'wallet' || methodType === 'bank_account') {
        alert('UPI, Wallet, and Bank Account payment methods will be available soon with Razorpay integration.');
        setShowAddModal(false);
        return;
      }
      
      // Fallback for cards if Stripe not configured
      alert('Stripe is not configured. Please configure Stripe publishable key.');
      setShowAddModal(false);
    } catch (err: any) {
      console.error('Error adding payment method:', err);
      setError(err.message || 'Failed to add payment method. Please try again.');
    }
  };

  const handleStripeCardSave = async (paymentMethodId: string) => {
    try {
      setError(null);
      
      // Save payment method to backend
      const response = await fetch(`${API_BASE}/api/payment-methods/save-stripe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_method_id: paymentMethodId,
          method_type: 'card',
          provider: 'stripe'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save payment method');
      }

      // Refresh payment methods list
      await fetchPaymentMethods();
      setShowStripeForm(false);
      setShowAddModal(false);
      setSelectedMethodType(null);
    } catch (err: any) {
      console.error('Error saving Stripe payment method:', err);
      setError(err.message || 'Failed to save payment method. Please try again.');
    }
  };

  const formatMethodDisplay = (method: PaymentMethod) => {
    switch (method.method_type) {
      case 'card':
        return `${method.card_brand?.toUpperCase() || 'Card'} •••• ${method.card_last4 || '****'}`;
      case 'upi':
        return `UPI: ${method.upi_id || 'N/A'}`;
      case 'bank_account':
        return `${method.bank_name || 'Bank'} •••• ${method.bank_account_last4 || '****'}`;
      case 'wallet':
        return `${method.wallet_type?.toUpperCase() || 'Wallet'}`;
      default:
        return 'Payment Method';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-100 flex flex-col"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blue/90 text-white p-6 overflow-hidden">
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Payment Methods</h2>
                  <p className="text-white/90 mt-1 text-sm">Manage your saved payment methods</p>
                </div>
                <button
                  onClick={onClose}
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all duration-200 group"
                >
                  <X className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                  <X className="w-5 h-5 text-red-600" />
                  <p className="text-red-600">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading payment methods...</p>
                </div>
              ) : (
                <>
                  {/* Add Payment Method Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Add Payment Method</span>
                    </button>
                  </div>

                  {/* Payment Methods List */}
                  {paymentMethods.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-12 text-center">
                      <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payment Methods</h3>
                      <p className="text-gray-600 mb-6">Add a payment method for faster checkout</p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Add Payment Method</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {paymentMethods.map((method) => (
                        <motion.div
                          key={method.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4 flex-1">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                method.method_type === 'card' ? 'bg-blue-100 text-blue-600' :
                                method.method_type === 'upi' ? 'bg-green-100 text-green-600' :
                                method.method_type === 'bank_account' ? 'bg-purple-100 text-purple-600' :
                                'bg-orange-100 text-orange-600'
                              }`}>
                                <CreditCard className="w-6 h-6" />
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {method.nickname || formatMethodDisplay(method)}
                                  </h3>
                                  {method.is_default && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                      Default
                                    </span>
                                  )}
                                </div>
                                
                                <p className="text-gray-600 text-sm mb-2">
                                  {formatMethodDisplay(method)}
                                </p>
                                
                                {method.method_type === 'card' && method.card_exp_month && method.card_exp_year && (
                                  <p className="text-gray-500 text-xs">
                                    Expires {method.card_exp_month}/{method.card_exp_year}
                                  </p>
                                )}
                                
                                {editingMethod?.id === method.id ? (
                                  <div className="mt-3 flex items-center space-x-2">
                                    <input
                                      type="text"
                                      value={formData.nickname || method.nickname || ''}
                                      onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                                      placeholder="Enter nickname"
                                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleUpdateNickname(method.id, formData.nickname || method.nickname || '')}
                                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingMethod(null);
                                        setFormData({});
                                      }}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="mt-3 flex items-center space-x-4">
                                    {!method.is_default && (
                                      <button
                                        onClick={() => handleSetDefault(method.id)}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                      >
                                        Set as default
                                      </button>
                                    )}
                                    <button
                                      onClick={() => {
                                        setEditingMethod(method);
                                        setFormData({ nickname: method.nickname || '' });
                                      }}
                                      className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                                    >
                                      Edit nickname
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <button
                              onClick={() => handleDelete(method.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete payment method"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Add Payment Method Modal */}
            {showAddModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      {showStripeForm ? 'Add Card' : 'Add Payment Method'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowAddModal(false);
                        setShowStripeForm(false);
                        setSelectedMethodType(null);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {showStripeForm && selectedMethodType === 'card' ? (
                    <Elements stripe={stripePromise}>
                      <StripeCardForm
                        onSuccess={handleStripeCardSave}
                        onCancel={() => {
                          setShowStripeForm(false);
                          setSelectedMethodType(null);
                        }}
                        apiBase={API_BASE}
                        token={token}
                      />
                    </Elements>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Method Type
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {(['card', 'upi', 'bank_account', 'wallet'] as const).map((type) => (
                            <button
                              key={type}
                              onClick={() => handleAddPaymentMethod(type)}
                              className="p-3 rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors text-left"
                            >
                              <div className="flex items-center space-x-2">
                                <CreditCard className="w-5 h-5 text-gray-600" />
                                <span className="text-sm font-medium capitalize">{type.replace('_', ' ')}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> Payment methods are saved securely through our payment providers.
                          {isStripeConfigured() && ' Cards are saved via Stripe.'}
                        </p>
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowAddModal(false);
                            setShowStripeForm(false);
                            setSelectedMethodType(null);
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentMethodsModal;

